import katex from 'katex';
import showdown from 'showdown';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

import { KATEX_STYLESHEET_URL } from '../../utils/katexStylesheet';

// Maximum length for LaTeX expressions to prevent ReDoS attacks
const MAX_MATH_CONTENT_LENGTH = 1000;

export const contentConverter = new showdown.Converter({
	tables: true,
	tablesHeaderId: true,
	ghCodeBlocks: true,
	tasklists: true,
	strikethrough: true,
	openLinksInNewWindow: true,
	backslashEscapesHTMLTags: true,
	simpleLineBreaks: true,
});

export const turndownService = new TurndownService({
	headingStyle: 'atx',
	hr: '---',
	codeBlockStyle: 'fenced',
});

turndownService.use(gfm);

/**
 * Replaces TipTap math node HTML with safe placeholders BEFORE Turndown
 * processes the HTML. This solves two problems:
 * 1. Turndown's blank-node detection (isBlank) short-circuits custom rules
 *    for empty elements — our math nodes have no text content.
 * 2. Turndown escapes \, _, *, [, ] etc. in text nodes, which would corrupt LaTeX.
 *
 * The LaTeX content is stored in an array and restored after Turndown finishes.
 */
function replaceMathHtmlWithPlaceholders(html: string): {
	html: string;
	mathEntries: Array<{ type: 'block' | 'inline'; latex: string }>;
} {
	const mathEntries: Array<{ type: 'block' | 'inline'; latex: string }> = [];
	let idx = 0;

	// Replace inline math: <span ... data-type="inline-math" ... data-latex="...">...</span>
	let result = html.replace(/<span\b[^>]*\bdata-type="inline-math"[^>]*>.*?<\/span>/gi, (match) => {
		const latexMatch = match.match(/data-latex="([^"]*)"/);
		if (!latexMatch) return match;
		const latex = unescapeMathHtmlEntities(latexMatch[1]);
		mathEntries.push({ type: 'inline', latex });
		return `MPHSTART${idx++}MPHEND`;
	});

	// Replace block math: <div ... data-type="block-math" ... data-latex="...">...</div>
	result = result.replace(/<div\b[^>]*\bdata-type="block-math"[^>]*>.*?<\/div>/gi, (match) => {
		const latexMatch = match.match(/data-latex="([^"]*)"/);
		if (!latexMatch) return match;
		const latex = unescapeMathHtmlEntities(latexMatch[1]);
		mathEntries.push({ type: 'block', latex });
		return `MPHSTART${idx++}MPHEND`;
	});

	return { html: result, mathEntries };
}

function unescapeMathHtmlEntities(str: string): string {
	return str
		.replace(/&quot;/g, '"')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&amp;/g, '&');
}

/**
 * Restores math placeholders in markdown with proper $...$ / $$...$$ syntax.
 */
function restoreMathInMarkdown(
	markdown: string,
	mathEntries: Array<{ type: 'block' | 'inline'; latex: string }>
): string {
	return markdown.replace(/MPHSTART(\d+)MPHEND/g, (_match, idxStr) => {
		const entry = mathEntries[Number.parseInt(idxStr, 10)];
		if (!entry) return '';
		if (entry.type === 'block') {
			return `\n$$\n${entry.latex}\n$$\n`;
		}
		return `$${entry.latex}$`;
	});
}

/**
 * Normalizes block math `$$` delimiters so they appear on their own lines.
 * remark-math (used by ReactMarkdown) requires `$$` on separate lines for block math.
 * AI-generated content often puts `$$` on the same line as the LaTeX content, e.g.:
 *   $$\frac{1}{2}$$          →  $$\n\frac{1}{2}\n$$
 *   $$\begin{pmatrix}...$$   →  $$\n\begin{pmatrix}...\n$$
 */
export function normalizeBlockMathDelimiters(markdown: string): string {
	// Opening $$: if $$ at start of line is followed by non-whitespace content, add newline
	let result = markdown.replace(/^(\$\$)([^\n$])/gm, '$1\n$2');
	// Closing $$: if $$ at end of line is preceded by non-whitespace content, add newline
	result = result.replace(/([^\n$])(\$\$)\s*$/gm, '$1\n$2');
	return result;
}

/**
 * `String.prototype.replace` for a global `pattern` that must not start at a
 * position preceded by `blockedPrefix` — i.e. a stand-in for a `(?<!x)` lookbehind.
 *
 * Safari only gained RegExp lookbehind in 16.4, and at our es2017 build target
 * esbuild silently rewrites an unsupported literal into `new RegExp(…)`, which
 * throws where it is evaluated (PLA-117).
 *
 * Guarding inside the replacer instead is NOT equivalent: it consumes the
 * rejected match, hiding any valid match starting inside it. Retrying one
 * character past a rejected start mirrors how the engine backtracks past a
 * failed lookbehind.
 */
function replaceNotPrecededBy(
	text: string,
	pattern: RegExp,
	blockedPrefix: string,
	replacer: (match: RegExpExecArray) => string
): string {
	let result = '';
	let copiedUpTo = 0;
	pattern.lastIndex = 0;
	// Both callers' patterns match at least three characters, so lastIndex always
	// advances and the loop terminates.
	for (let match = pattern.exec(text); match !== null; match = pattern.exec(text)) {
		if (text[match.index - 1] === blockedPrefix) {
			pattern.lastIndex = match.index + 1;
			continue;
		}
		result += text.slice(copiedUpTo, match.index) + replacer(match);
		copiedUpTo = match.index + match[0].length;
		pattern.lastIndex = copiedUpTo;
	}
	return result + text.slice(copiedUpTo);
}

/**
 * Extracts math blocks from markdown and replaces them with safe placeholders.
 * Returns the cleaned markdown and an array of extracted math entries.
 * This prevents Showdown from corrupting LaTeX content (e.g. interpreting
 * backslashes as escape sequences, underscores as emphasis, etc.).
 */
function extractMathFromMarkdown(markdown: string): {
	cleaned: string;
	mathEntries: Array<{ type: 'block' | 'inline'; latex: string }>;
} {
	const mathEntries: Array<{ type: 'block' | 'inline'; latex: string }> = [];
	let idx = 0;

	// First extract block math: $$ on its own line, content, $$ on its own line
	let cleaned = markdown.replace(/\$\$\s*\n([\s\S]*?)\n\s*\$\$/g, (_match, latex: string) => {
		const placeholder = `<div data-math-placeholder="${idx}"></div>`;
		mathEntries.push({ type: 'block', latex: latex.trim() });
		idx++;
		return placeholder;
	});

	// Also extract single-line block math: $$...$$ on one line
	// Limit match length to prevent catastrophic backtracking (ReDoS)
	cleaned = replaceNotPrecededBy(
		cleaned,
		new RegExp(`\\$\\$([^$\\n]{1,${MAX_MATH_CONTENT_LENGTH}}?)\\$\\$(?!\\$)`, 'g'),
		'$',
		(match) => {
			const placeholder = `<div data-math-placeholder="${idx}"></div>`;
			mathEntries.push({ type: 'block', latex: match[1].trim() });
			idx++;
			return placeholder;
		}
	);

	// Then extract inline math: $...$ (not $$, and not dollar amounts like $100)
	// Limit match length to prevent catastrophic backtracking (ReDoS)
	cleaned = replaceNotPrecededBy(
		cleaned,
		new RegExp(`\\$(?!\\$)(?!\\d+\\$)([^$\\n]{1,${MAX_MATH_CONTENT_LENGTH}}?)\\$(?!\\$)(?!\\d)`, 'g'),
		'$',
		(match) => {
			const placeholder = `<span data-math-placeholder="${idx}"></span>`;
			mathEntries.push({ type: 'inline', latex: match[1].trim() });
			idx++;
			return placeholder;
		}
	);

	return { cleaned, mathEntries };
}

/**
 * Restores math placeholders in HTML with proper TipTap math node tags.
 * The LaTeX is HTML-attribute-escaped to safely embed in data-latex attributes.
 */
function restoreMathPlaceholders(
	html: string,
	mathEntries: Array<{ type: 'block' | 'inline'; latex: string }>
): string {
	// Showdown may wrap block placeholders in <p> tags — unwrap them
	let result = html.replace(
		/<p>\s*<div data-math-placeholder="(\d+)"><\/div>\s*<\/p>/g,
		(_match, idxStr) => {
			const entry = mathEntries[Number.parseInt(idxStr, 10)];
			if (!entry) return '';
			const escaped = escapeMathLatex(entry.latex);
			return `<div data-type="block-math" data-latex="${escaped}"></div>`;
		}
	);

	// Handle remaining placeholders (inline spans or unwrapped divs)
	result = result.replace(
		/<(div|span) data-math-placeholder="(\d+)"><\/(div|span)>/g,
		(_match, _tag, idxStr) => {
			const entry = mathEntries[Number.parseInt(idxStr, 10)];
			if (!entry) return '';
			const escaped = escapeMathLatex(entry.latex);
			if (entry.type === 'block') {
				return `<div data-type="block-math" data-latex="${escaped}"></div>`;
			}
			return `<span data-type="inline-math" data-latex="${escaped}"></span>`;
		}
	);

	return result;
}

function escapeMathLatex(latex: string): string {
	return latex
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

function unescapeMathLatex(escaped: string): string {
	return escaped
		.replace(/&gt;/g, '>')
		.replace(/&lt;/g, '<')
		.replace(/&quot;/g, '"')
		.replace(/&amp;/g, '&');
}

export function normalizeTiptapHtml(html: string): string {
	return (
		html
			// remove ProseMirror trailing breaks
			.replace(/<br class="ProseMirror-trailingBreak">/g, '')
			// unwrap <p> inside table cells
			.replace(/<td[^>]*><p>(.*?)<\/p><\/td>/g, '<td>$1</td>')
			.replace(/<th[^>]*><p>(.*?)<\/p><\/th>/g, '<th>$1</th>')
	);
}

const sanitizeWidthValue = (value?: string | null): string | null => {
	if (!value) return null;
	const trimmed = value.trim();
	if (trimmed === '' || trimmed === '100%') return null;
	if (/^\d+(\.\d+)?(px|%)$/.test(trimmed)) {
		return trimmed;
	}
	return null;
};

/**
 * Extracts image width information from HTML images.
 *
 * This function parses HTML to find all images and extract their width
 * from either inline styles or data-width attributes.
 *
 * @param html - The HTML string to parse
 * @returns A Map where keys are image indices and values are width strings
 *
 * @example
 * ```typescript
 * const html = '<img src="test.jpg" style="width: 50%">';
 * const widths = extractImageWidthsFromHtml(html);
 * // Returns: Map { 0 => '50%' }
 * ```
 */
export function extractImageWidthsFromHtml(html: string): Map<number, string> {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');
	const images = doc.querySelectorAll('img');
	const widthsMap = new Map<number, string>();

	images.forEach((img, index) => {
		// Try data-width first, then style width
		const dataWidth = img.getAttribute('data-width');
		const styleWidth = img.style.width;
		const width = sanitizeWidthValue(dataWidth || styleWidth);

		// Only store if we have a safe custom width
		if (width) {
			widthsMap.set(index, width);
		}
	});

	return widthsMap;
}

/**
 * Extracts column width information from HTML tables.
 *
 * This function parses HTML to find all tables and extract their column widths
 * from either <colgroup>/<col> elements or inline styles on cells.
 *
 * @param html - The HTML string to parse
 * @returns A Map where keys are table indices and values are arrays of width strings
 *
 * @example
 * ```typescript
 * const html = '<table><colgroup><col style="width: 30%"><col style="width: 70%"></colgroup>...</table>';
 * const widths = extractTableWidthsFromHtml(html);
 * // Returns: Map { 0 => ['30%', '70%'] }
 * ```
 */
export function extractTableWidthsFromHtml(html: string): Map<number, string[]> {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');
	const tables = doc.querySelectorAll('table');
	const widthsMap = new Map<number, string[]>();

	tables.forEach((table, index) => {
		const widths: string[] = [];

		// Try to extract column widths from colgroup or col elements
		const colgroup = table.querySelector('colgroup');

		if (colgroup) {
			const cols = colgroup.querySelectorAll('col');
			cols.forEach((col) => {
				const width =
					col.getAttribute('style')?.match(/width:\s*([^;]+)/)?.[1] ||
					col.getAttribute('width') ||
					'auto';
				widths.push(width.trim());
			});
		} else {
			// Try to get widths from first row cells
			const firstRow = table.querySelector('tr');
			if (firstRow) {
				const cells = firstRow.querySelectorAll('th, td');
				cells.forEach((cell) => {
					const cellElement = cell as HTMLElement;
					const width = cellElement.style.width || 'auto';
					widths.push(width);
				});
			}
		}

		// Only store if we have meaningful widths (not all 'auto')
		const hasCustomWidths = widths.some((w) => w !== 'auto' && w !== '');
		if (hasCustomWidths) {
			widthsMap.set(index, widths);
		}
	});

	return widthsMap;
}

/**
 * Injects HTML comments containing image width information into Markdown.
 *
 * This function scans the Markdown for image syntax and adds width metadata
 * as HTML comments immediately before each image that has a custom width.
 *
 * @param markdown - The Markdown string containing images
 * @param widthsMap - Map of image indices to their widths
 * @returns The Markdown with width comments injected before relevant images
 *
 * @example
 * ```typescript
 * const markdown = '![alt](image.jpg)';
 * const widthsMap = new Map([[0, '50%']]);
 * const result = injectImageWidthsIntoMarkdown(markdown, widthsMap);
 * // Returns: '<!-- img-width: 50% -->![alt](image.jpg)'
 * ```
 */
export function injectImageWidthsIntoMarkdown(
	markdown: string,
	widthsMap: Map<number, string>
): string {
	if (widthsMap.size === 0) {
		return markdown;
	}

	let imageIndex = 0;
	// Match markdown images: ![alt](src)
	const imagePattern = /!\[([^\]]*)\]\(([^)]+)\)/g;

	const result = markdown.replace(imagePattern, (match) => {
		const width = widthsMap.get(imageIndex);
		imageIndex++;

		if (width) {
			return `<!-- img-width: ${width} -->${match}`;
		}
		return match;
	});

	return result;
}

/**
 * Extracts image widths from markdown comments and removes the comments.
 *
 * This scans for `<!-- img-width: X -->` comments and applies them to the
 * next markdown image in order, returning a cleaned markdown string and
 * an index-based map of image widths.
 */
export function extractImageWidthsFromMarkdown(markdown: string): {
	cleanedMarkdown: string;
	widthsByIndex: Map<number, string>;
} {
	const widthsByIndex = new Map<number, string>();
	const tokenPattern = /<!--\s*img-width:\s*([^>]+?)\s*-->|!\[[^\]]*]\([^)\n]+\)/g;
	let imageIndex = 0;
	let pendingWidth: string | null = null;
	let match: RegExpExecArray | null;

	// biome-ignore lint/suspicious/noAssignInExpressions: pre-existing assignment-in-expression; extracting it risks changing evaluation order
	while ((match = tokenPattern.exec(markdown)) !== null) {
		const widthValue = match[1];
		if (widthValue) {
			pendingWidth = sanitizeWidthValue(widthValue);
			continue;
		}

		if (pendingWidth) {
			widthsByIndex.set(imageIndex, pendingWidth);
			pendingWidth = null;
		}
		imageIndex += 1;
	}

	const cleanedMarkdown = markdown.replace(/<!--\s*img-width:\s*[^>]+?\s*-->\s*/g, '');

	return { cleanedMarkdown, widthsByIndex };
}

/**
 * Injects HTML comments containing column width information into Markdown tables.
 *
 * This function scans the Markdown for table structures and adds width metadata
 * as HTML comments immediately before each table that has custom widths.
 *
 * @param markdown - The Markdown string containing tables
 * @param widthsMap - Map of table indices to their column widths
 * @returns The Markdown with width comments injected before relevant tables
 *
 * @example
 * ```typescript
 * const markdown = '| Col 1 | Col 2 |\n|-------|-------|\n| A     | B     |';
 * const widthsMap = new Map([[0, ['30%', '70%']]]);
 * const result = injectWidthCommentsIntoMarkdown(markdown, widthsMap);
 * // Returns: '<!-- table-col-widths: 30%,70% -->\n| Col 1 | Col 2 |\n...'
 * ```
 */
export function injectWidthCommentsIntoMarkdown(
	markdown: string,
	widthsMap: Map<number, string[]>
): string {
	if (widthsMap.size === 0) {
		return markdown;
	}

	const lines = markdown.split('\n');
	const result: string[] = [];
	let tableIndex = 0;
	let inTable = false;

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		const isTableLine = line.trim().startsWith('|');

		if (isTableLine && !inTable) {
			// Starting a new table
			const widths = widthsMap.get(tableIndex);
			if (widths) {
				result.push(`<!-- table-col-widths: ${widths.join(',')} -->`);
			}
			inTable = true;
		} else if (!isTableLine && inTable) {
			// Exiting a table
			inTable = false;
			tableIndex++;
		}

		result.push(line);
	}

	return result.join('\n');
}

/**
 * Converts HTML to Markdown while preserving table column widths and image widths.
 *
 * This function performs a multi-step process:
 * 1. Extract width information from HTML tables and images
 * 2. Convert HTML to Markdown using Turndown with GFM plugin
 * 3. Inject width information as HTML comments before tables and images
 *
 * The width comments are preserved in Markdown and can be used to restore
 * the widths when converting back to HTML.
 *
 * @param html - The HTML string to convert
 * @returns Markdown with embedded width metadata
 *
 * @example
 * ```typescript
 * const html = '
 *   <img src="test.jpg" style="width: 50%">
 *   <table>
 *     <colgroup>
 *       <col style="width: 25%">
 *       <col style="width: 75%">
 *     </colgroup>
 *     ...
 *   </table>
 * ';
 * const markdown = htmlToMarkdown(html);
 * // Returns markdown with width comments for both images and tables
 * ```
 */
export function htmlToMarkdown(html: string): string {
	let cleaned = normalizeTiptapHtml(html);

	// Replace math HTML with safe placeholders before Turndown processes it.
	// This prevents: (1) Turndown dropping empty math elements, (2) Turndown
	// escaping \, _, *, etc. in LaTeX content.
	const { html: mathSafeHtml, mathEntries } = replaceMathHtmlWithPlaceholders(cleaned);
	cleaned = mathSafeHtml;

	// Extract width information before conversion
	const tableWidthsMap = extractTableWidthsFromHtml(cleaned);
	const imageWidthsMap = extractImageWidthsFromHtml(cleaned);

	// Convert to markdown
	let markdown = turndownService.turndown(cleaned);

	// Restore math placeholders with $...$ / $$...$$ syntax
	markdown = restoreMathInMarkdown(markdown, mathEntries);

	// Inject width comments into the markdown
	markdown = injectWidthCommentsIntoMarkdown(markdown, tableWidthsMap);
	markdown = injectImageWidthsIntoMarkdown(markdown, imageWidthsMap);

	return markdown;
}

/**
 * Restores image widths from HTML comments in the HTML output.
 *
 * This function searches for image width metadata comments (created by htmlToMarkdown)
 * and adds style attributes to the following img tags.
 *
 * @param html - The HTML string that may contain width comments
 * @returns HTML with width comments removed and styles applied to images
 *
 * @example
 * ```typescript
 * const html = '<!-- img-width: 50% --><img src="test.jpg">';
 * const result = restoreImageWidths(html);
 * // Returns: '<img src="test.jpg" style="width: 50%">'
 * ```
 */
export function restoreImageWidths(html: string): string {
	// Find patterns like: <!-- img-width: 50% -->
	// The comment might be followed by whitespace, paragraph tags, or other HTML before the <img>
	const commentPattern = /<!-- img-width: ([^>]+) -->/g;

	// First, extract all width comments and store them in order
	const widths: string[] = [];
	let match: RegExpExecArray | null;
	// biome-ignore lint/suspicious/noAssignInExpressions: pre-existing assignment-in-expression; extracting it risks changing evaluation order
	while ((match = commentPattern.exec(html)) !== null) {
		widths.push(match[1].trim());
	}

	// Remove all width comments
	let result = html.replace(commentPattern, '');

	// Now apply widths to images in order
	let widthIndex = 0;
	result = result.replace(/<img([^>]*)>/g, (imgTag, attributes) => {
		if (widthIndex < widths.length) {
			const width = sanitizeWidthValue(widths[widthIndex++]);
			if (!width) {
				return imgTag;
			}

			// Check if the img tag already has a style attribute
			if (attributes.includes('style=')) {
				return imgTag.replace(/style="([^"]*)"/, `style="$1; width: ${width}"`);
			}
			// Add new style attribute
			return `<img${attributes} style="width: ${width}">`;
		}
		return imgTag;
	});

	return result;
}

/**
 * Restores table column widths from HTML comments in the HTML output.
 *
 * This function searches for width metadata comments (created by htmlToMarkdown)
 * and converts them into proper <colgroup> elements with <col> style attributes.
 *
 * @param html - The HTML string that may contain width comments
 * @returns HTML with width comments replaced by <colgroup> elements
 *
 * @example
 * ```typescript
 * const html = '<!-- table-col-widths: 30%,70% -->\n<table><tr>...</tr></table>';
 * const result = restoreTableColumnWidths(html);
 * // Returns: '<table><colgroup><col style="width: 30%"><col style="width: 70%"></colgroup><tr>...</tr></table>'
 * ```
 */
export function restoreTableColumnWidths(html: string): string {
	// Find patterns like: <!-- table-col-widths: 30%,40%,30% --> followed by <table>
	const pattern = /<!-- table-col-widths: ([^>]+) -->\s*(<table[^>]*>)/g;

	return html.replace(pattern, (_match, widths, tableTag) => {
		const widthArray = widths.split(',').map((w: string) => w.trim());

		// Create colgroup element
		const colgroupCols = widthArray.map((width: string) => `<col style="width: ${width}">`).join('');
		const colgroup = `<colgroup>${colgroupCols}</colgroup>`;

		// Remove the comment and insert colgroup right after the table tag
		return `${tableTag}${colgroup}`;
	});
}

/**
 * Converts Markdown to HTML while restoring table column widths and image widths.
 *
 * This function converts Markdown to HTML using Showdown, then processes
 * any width metadata comments to restore column widths via <colgroup> elements
 * and image widths via style attributes.
 *
 * This works in conjunction with htmlToMarkdown() to preserve widths
 * through the HTML → Markdown → HTML conversion cycle.
 *
 * @param markdown - The Markdown string to convert (may contain width comments)
 * @returns HTML with restored widths
 *
 * @example
 * ```typescript
 * const markdown = '
 *   <!-- img-width: 50% -->![alt](img.jpg)
 *   <!-- table-col-widths: 25%,50%,25% -->
 *   | A | B | C |
 *   |---|---|---|
 *   | 1 | 2 | 3 |
 * ';
 * const html = convertMarkdownToHtml(markdown);
 * // Returns HTML with restored image and table widths
 * ```
 */
export const convertMarkdownToHtml = (markdown: string): string => {
	// 0. Normalize $$ to be on their own lines so the extraction regex works
	const normalized = normalizeBlockMathDelimiters(markdown);
	// 1. Extract math into safe placeholders so Showdown can't corrupt LaTeX
	//    (backslash escapes, underscore emphasis, etc.)
	const { cleaned, mathEntries } = extractMathFromMarkdown(normalized);
	// 2. Let Showdown convert the rest of the markdown to HTML
	let html = contentConverter.makeHtml(cleaned);
	// 3. Restore math placeholders with proper TipTap math node HTML
	html = restoreMathPlaceholders(html, mathEntries);
	html = restoreTableColumnWidths(html);
	html = restoreImageWidths(html);
	return html;
};

/**
 * Restores math placeholders in HTML with rendered KaTeX output instead of TipTap nodes.
 * Used for PDF export where the backend renderer needs actual rendered math.
 * Outputs both HTML and MathML for maximum compatibility with different PDF renderers.
 */
function restoreMathPlaceholdersAsKatex(
	html: string,
	mathEntries: Array<{ type: 'block' | 'inline'; latex: string }>
): string {
	// Handle block math (unwrap from <p> if needed)
	let result = html.replace(
		/<p>\s*<div data-math-placeholder="(\d+)"><\/div>\s*<\/p>/g,
		(_match, idxStr) => {
			const entry = mathEntries[Number.parseInt(idxStr, 10)];
			if (!entry) return '';
			try {
				return katex.renderToString(entry.latex, {
					displayMode: true,
					throwOnError: false,
					output: 'htmlAndMathml', // Output both HTML and MathML for maximum compatibility
				});
			} catch {
				return `<div class="math-error">$$${entry.latex}$$</div>`;
			}
		}
	);

	// Handle inline math and remaining placeholders
	result = result.replace(
		/<(?:span|div) data-math-placeholder="(\d+)"><\/(?:span|div)>/g,
		(_match, idxStr) => {
			const entry = mathEntries[Number.parseInt(idxStr, 10)];
			if (!entry) return '';
			const displayMode = entry.type === 'block';
			try {
				return katex.renderToString(entry.latex, {
					displayMode,
					throwOnError: false,
					output: 'htmlAndMathml', // Output both HTML and MathML for maximum compatibility
				});
			} catch {
				const delim = displayMode ? '$$' : '$';
				return `<span class="math-error">${delim}${entry.latex}${delim}</span>`;
			}
		}
	);

	return result;
}

/**
 * Converts Markdown with math to HTML suitable for PDF export.
 * Math formulas are rendered as KaTeX HTML that can be displayed by PDF renderers.
 *
 * @param markdown - The Markdown string to convert (with $...$ and $$...$$ math)
 * @returns HTML with rendered KaTeX math and a <style> tag for KaTeX CSS
 *
 * @example
 * ```typescript
 * const markdown = 'Einstein: $E=mc^2$\n\n$$\\sum_{i=1}^n i = \\frac{n(n+1)}{2}$$';
 * const html = convertMarkdownToHtmlForPdf(markdown);
 * // Returns HTML with KaTeX-rendered math and embedded KaTeX CSS
 * ```
 */
export const convertMarkdownToHtmlForPdf = (markdown: string): string => {
	// 0. Normalize $$ to be on their own lines
	const normalized = normalizeBlockMathDelimiters(markdown);
	// 1. Extract math into safe placeholders
	const { cleaned, mathEntries } = extractMathFromMarkdown(normalized);
	// 2. Let Showdown convert the markdown to HTML
	let html = contentConverter.makeHtml(cleaned);
	// 3. Restore math as rendered KaTeX HTML (not TipTap nodes)
	html = restoreMathPlaceholdersAsKatex(html, mathEntries);
	html = restoreTableColumnWidths(html);
	html = restoreImageWidths(html);

	// 4. Add KaTeX CSS inline (critical for PDF rendering)
	const katexCss = `
    <link rel="stylesheet" href="${KATEX_STYLESHEET_URL}" crossorigin="anonymous">
  `;

	return katexCss + html;
};

/**
 * Converts TipTap editor HTML (with math nodes) to HTML suitable for PDF export.
 * Finds all TipTap math nodes and renders them using KaTeX.
 * Outputs both HTML and MathML for maximum compatibility with different PDF renderers.
 *
 * @param html - HTML from TipTap editor containing
 *               <div data-type="block-math"> and <span data-type="inline-math"> nodes
 * @returns HTML with rendered KaTeX (HTML+MathML) and embedded KaTeX CSS
 */
export const convertTipTapHtmlForPdf = (html: string): string => {
	let result = html;

	// Convert block math nodes
	result = result.replace(
		/<div data-type="block-math" data-latex="([^"]*)"[^>]*><\/div>/g,
		(_match, escapedLatex) => {
			const latex = unescapeMathLatex(escapedLatex);
			try {
				return katex.renderToString(latex, {
					displayMode: true,
					throwOnError: false,
					output: 'htmlAndMathml', // Output both HTML and MathML for maximum compatibility
				});
			} catch {
				return `<div class="math-error">$$${latex}$$</div>`;
			}
		}
	);

	// Convert inline math nodes
	result = result.replace(
		/<span data-type="inline-math" data-latex="([^"]*)"[^>]*><\/span>/g,
		(_match, escapedLatex) => {
			const latex = unescapeMathLatex(escapedLatex);
			try {
				return katex.renderToString(latex, {
					displayMode: false,
					throwOnError: false,
					output: 'htmlAndMathml', // Output both HTML and MathML for maximum compatibility
				});
			} catch {
				return `<span class="math-error">$${latex}$</span>`;
			}
		}
	);

	// Add KaTeX CSS
	const katexCss = `
    <link rel="stylesheet" href="${KATEX_STYLESHEET_URL}" crossorigin="anonymous">
  `;

	return katexCss + result;
};
